import {
  FeeDetailDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { PDFFeeAnalysisConfig } from "./configs";
import { FeeAnalysisLabelConstants } from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import {
  PageTemplate,
  PDFFeeAnalysis,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import {
  MARGIN_DEFAULT,
  PAGE_BODY_HEIGHT_WITHOUT_HEADING,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import { PDFFeeAnalysisSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-analysis/index";
import { cloneDeep } from "lodash";
import { pagesHandler } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";

const processFeeEstimatesData = (
  spaceAvailable: number,
  data: FeeSubProductDTO[],
  heatmapMode?: FeeHeatmapMode,
  showFeeAnalysisText?: boolean,
  showFeeEstimatesText?: boolean,
) => {
  let titleHeight = PDFFeeAnalysisConfig.DefaultFeeTitleHeight;
  if (showFeeAnalysisText) titleHeight += 25;
  if (showFeeEstimatesText) titleHeight += 17;

  const additionalHeight =
    PDFFeeAnalysisConfig.MarginHeight +
    PDFFeeAnalysisConfig.FooterHeight +
    (heatmapMode === FeeHeatmapMode.Projection
      ? PDFFeeAnalysisConfig.LabelLineProjectionHeight
      : PDFFeeAnalysisConfig.LabelLineDetailHeight) *
      2 +
    titleHeight;

  const remainingHeight = spaceAvailable - additionalHeight;
  const numberRowAvailable = Math.floor(
    remainingHeight / PDFFeeAnalysisConfig.RowHeight,
  );

  let occupyHeight = 0;
  let hasContinue = false;
  let dataSeparated: FeeSubProductDTO[] = [];

  if (numberRowAvailable > 0) {
    if (data.length > numberRowAvailable) {
      dataSeparated = data.splice(0, numberRowAvailable);
      occupyHeight = spaceAvailable;
      hasContinue = true;
    } else if (data.length === numberRowAvailable) {
      dataSeparated = data;
      occupyHeight = spaceAvailable;
      hasContinue = false;
    } else if (data.length < numberRowAvailable) {
      dataSeparated = data;
      occupyHeight =
        additionalHeight + data.length * PDFFeeAnalysisConfig.RowHeight;
      hasContinue = false;
    }
  } else {
    if (numberRowAvailable === 0 && data.length === 0) {
      hasContinue = false;
      occupyHeight = additionalHeight;
    } else {
      hasContinue = true;
    }
  }

  return {
    name: "Fee estimates",
    occupyHeight: occupyHeight,
    dataSeparated: dataSeparated,
    hasContinue: hasContinue,
  };
};

export const calculateFeeDetailColumnWidth = (
  totalWidth: number,
  feeDetails: FeeDetailDTO[],
) => {
  const totalColumns = feeDetails.length;
  const columnWidth = Number(totalWidth / totalColumns);

  const hasTooManyFeeColumns =
    feeDetails.length > PDFFeeAnalysisConfig.MaxFeeDetailsColumnToDisplayLogo;
  const hasPYSColumn = !!feeDetails.find(
    (feeDetail) => feeDetail.name === FeeAnalysisLabelConstants.PYSRefund,
  );

  if (hasTooManyFeeColumns && hasPYSColumn) {
    const pysColumnWidth = columnWidth * (2 / 3);
    const otherColumnWidth = (totalWidth - pysColumnWidth) / (totalColumns - 1);

    return { pysColumnWidth: Math.floor(pysColumnWidth), otherColumnWidth };
  }
  return { pysColumnWidth: columnWidth, otherColumnWidth: columnWidth };
};

const fittedPreviousSection = (
  pages: PageTemplate[],
  spaceAvailable: number,
  feeAnalysis: PDFFeeAnalysis,
  heatmapMode: FeeHeatmapMode,
  isFamilyLevel?: boolean,
  tablePerformanceScope?: FeesPerformanceScope,
) => {
  const {
    totalSubProductHidden,
    totalPortfolioValue,
    feesDisplayStyle,
    showWarningCaptions,
  } = feeAnalysis;
  let subProductData = cloneDeep(feeAnalysis.subProducts);
  let data = processFeeEstimatesData(
    spaceAvailable,
    subProductData ?? [],
    heatmapMode,
    true,
    true,
  );

  const showFeeEstimatesText = heatmapMode !== FeeHeatmapMode.Projection;

  if (!data.hasContinue && data.occupyHeight) {
    let feeEstimatesComp = (
      <PDFFeeAnalysisSection
        totalSubProductHidden={totalSubProductHidden}
        subProducts={data.dataSeparated ?? []}
        totalPortfolioValue={totalPortfolioValue}
        mode={feesDisplayStyle as any}
        heatmapMode={heatmapMode}
        showFeeEstimatesText={showFeeEstimatesText}
        showWarningCaptions={showWarningCaptions}
        isFamilyLevel={isFamilyLevel}
        feesPerformanceScope={tablePerformanceScope}
        isExcludePerformanceFees={feeAnalysis?.isExcludePerformanceFees}
      />
    );
    pagesHandler(pages, feeEstimatesComp, true);

    return data.occupyHeight;
  }
  return -1;
};

const feeEstimatesModeHandler = (
  pages: PageTemplate[],
  spaceAvailable: number,
  feeAnalysis: PDFFeeAnalysis,
  heatmapMode: FeeHeatmapMode,
  allowShowFeeEstimatesText: boolean,
  isFamilyLevel?: boolean,
  tablePerformanceScope?: FeesPerformanceScope,
) => {
  const pageBodyHeight = PAGE_BODY_HEIGHT_WITHOUT_HEADING;
  const {
    totalSubProductHidden,
    totalPortfolioValue,
    feesDisplayStyle,
    showWarningCaptions,
  } = feeAnalysis;

  const fittedHeight = fittedPreviousSection(
    pages,
    spaceAvailable,
    feeAnalysis,
    heatmapMode,
    isFamilyLevel,
    tablePerformanceScope,
  );

  if (fittedHeight !== -1) return spaceAvailable - fittedHeight;

  const feeEstimatesComp = (
    subProducts: FeeSubProductDTO[],
    showFeeAnalysisText?: boolean,
    showFeeEstimatesText?: boolean,
  ) => {
    return (
      <PDFFeeAnalysisSection
        totalSubProductHidden={totalSubProductHidden}
        subProducts={subProducts ?? []}
        totalPortfolioValue={totalPortfolioValue}
        mode={feesDisplayStyle as any}
        heatmapMode={heatmapMode}
        showWarningCaptions={showWarningCaptions}
        showFeeAnalysisText={showFeeAnalysisText}
        showFeeEstimatesText={showFeeEstimatesText}
        isFamilyLevel={isFamilyLevel}
        feesPerformanceScope={tablePerformanceScope}
        isExcludePerformanceFees={feeAnalysis?.isExcludePerformanceFees}
      />
    );
  };

  const isAppend = spaceAvailable > pageBodyHeight / 2;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  let subProductData = cloneDeep(feeAnalysis.subProducts);
  const showFeeAnalysisText = !isAppend; //Fee analysis
  const showFeeEstimatesText = allowShowFeeEstimatesText; //Fee estimate

  let data = processFeeEstimatesData(
    spaceAvailable,
    subProductData ?? [],
    heatmapMode,
    showFeeAnalysisText,
    showFeeEstimatesText,
  );

  pagesHandler(
    pages,
    feeEstimatesComp(
      data?.dataSeparated,
      showFeeAnalysisText,
      showFeeEstimatesText,
    ),
    isAppend,
  );

  while (data.hasContinue) {
    spaceAvailable = pageBodyHeight;

    data = processFeeEstimatesData(
      spaceAvailable,
      subProductData ?? [],
      heatmapMode,
      true,
      false,
    );
    pagesHandler(
      pages,
      feeEstimatesComp(data?.dataSeparated, true, false),
      false,
    );
  }

  return spaceAvailable - data.occupyHeight - MARGIN_DEFAULT;
};

export const feesAnalysisPageHandler = (
  pages: PageTemplate[],
  spaceAvailable: number,
  feeAnalysis: PDFFeeAnalysis,
  isFamilyLevel?: boolean,
  tablePerformanceScope?: FeesPerformanceScope,
) => {
  spaceAvailable = feeEstimatesModeHandler(
    pages,
    spaceAvailable,
    feeAnalysis,
    FeeHeatmapMode.Detail,
    true,
    isFamilyLevel,
    tablePerformanceScope,
  );

  spaceAvailable = feeEstimatesModeHandler(
    pages,
    spaceAvailable,
    feeAnalysis,
    FeeHeatmapMode.Projection,
    false,
    isFamilyLevel,
    tablePerformanceScope,
  );

  return spaceAvailable;
};
